import React from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import minusCircleTokens from '@amzn/meridian-tokens/base/icon/minus-circle';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import Input from '@amzn/meridian/input';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';
import TimePicker from '@amzn/meridian/time-picker';
import moment from 'moment-timezone';

import { chainWalk } from '../../../../utils/helpers';
import {
  getShiftSchedules,
  getTimelineForViewPlan,
  isFeatureFlagEnabled,
  displayBusinessTabHeader,
  timeStringToEpoch,
  epochToTimeString
} from '../../utils';
import {
  DEFAULT_SPEED,
  SSD_OVERRIDE_REASON_CODES,
  SSD_FEATURE_FLAGS_LIST,
  SECONDS_IN_AN_HOUR,
  SECONDS_IN_A_DAY
} from '../../../../utils/constants';
import { SINGLES_ROLES, getSinglesEnabled } from '../../common/ssd/SinglesUtils';
import NavigationButtons from '../../../commonComponents/NavigationButtons';

const VerifyNewHires = ({ currentUser, plan, setPlan, onGoBack, onSubmit, onCancel }) => {
  const timeline = getTimelineForViewPlan(plan);
  const shiftSchedules = getShiftSchedules(timeline);
  const singlesEnabled = getSinglesEnabled(plan);
  const { timezone = moment.tz.guess() } = JSON.parse(plan.input.adminSettings);
  const isGroceryEnabled = isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.groceryEnabled, false);
  const isRushIndirectEnabled = isFeatureFlagEnabled(
    plan.input.adminSettings,
    SSD_FEATURE_FLAGS_LIST.rushIndirectRulesEnabled,
    true
  );
  const isNarrowWindowsEnabled = isFeatureFlagEnabled(
    plan.input.adminSettings,
    SSD_FEATURE_FLAGS_LIST.narrowWindowsEnabled,
    false
  );
  const newHireRoles = chainWalk(
    () =>
      plan.input.ssdShiftPlanInput.newHiresList
        .filter((role) => singlesEnabled || SINGLES_ROLES.indexOf(role.role.name) < 0)
        .filter((role) => !isRushIndirectEnabled || role.role.speed === null || role.role.speed === DEFAULT_SPEED),
    []
  );

  const onAddHeadcountOverride = () => {
    const unoverriddenRoles = newHireRoles.filter(
      (role) => chainWalk(() => role.overrides.overrideKeyValueMap.headcount, null) == null
    );
    if (unoverriddenRoles.length <= 0) return false;
    const newRole = unoverriddenRoles[0];
    if (!newRole.overrides) {
      newRole.overrides = {};
    }
    if (!newRole.overrides.overrideKeyValueMap) {
      newRole.overrides.overrideKeyValueMap = {};
    }
    newRole.overrides.overrideKeyValueMap.headcount = '0';
    newRole.overrides.reason = SSD_OVERRIDE_REASON_CODES.NO_REASON_GIVEN.value;
    newRole.overrides.timestamp = parseInt(Date.now() / 1000);
    newRole.overrides.user = currentUser;
    setPlan({ ...plan });
  };

  const onDeleteHeadcountOverride = (role) => {
    if (role.overrides) {
      delete role.overrides.overrideKeyValueMap;
      delete role.overrides.users;
      delete role.overrides.reason;
    }
    setPlan({ ...plan });
  };
  const onChangeProcessRole = (prevRole, newRoleName) => {
    const newRole = newHireRoles.find((r) => r.role.name === newRoleName);
    if (newRole && prevRole) {
      if (!newRole.overrides) {
        newRole.overrides = {};
      }
      if (!newRole.overrides.overrideKeyValueMap) {
        newRole.overrides.overrideKeyValueMap = {};
      }
      newRole.overrides.overrideKeyValueMap.headcount = prevRole.overrides.overrideKeyValueMap.headcount;
      newRole.overrides.reason = SSD_OVERRIDE_REASON_CODES.NO_REASON_GIVEN.value;
      newRole.overrides.timestamp = parseInt(Date.now() / 1000);
      newRole.overrides.user = currentUser;
      delete prevRole.overrides.overrideKeyValueMap;
      delete prevRole.overrides.users;
      delete prevRole.overrides.reason;
      setPlan({ ...plan });
    }
  };
  const onChangeHeadcount = (role, newVal) => {
    if (!role.overrides) {
      role.overrides = {};
    }
    if (!role.overrides.overrideKeyValueMap) {
      role.overrides.overrideKeyValueMap = {};
    }
    role.overrides.overrideKeyValueMap.headcount = newVal + '';
    role.overrides.reason = SSD_OVERRIDE_REASON_CODES.NO_REASON_GIVEN.value;
    role.overrides.timestamp = parseInt(Date.now() / 1000);
    role.overrides.user = currentUser;
    setPlan({ ...plan });
  };

  // Helper function to update role override metadata
  const updateRoleOverride = (role) => {
    if (!role.overrides) {
      role.overrides = {};
    }
    if (!role.overrides.overrideKeyValueMap) {
      role.overrides.overrideKeyValueMap = {};
    }
    role.overrides.reason = SSD_OVERRIDE_REASON_CODES.NO_REASON_GIVEN.value;
    role.overrides.timestamp = parseInt(Date.now() / 1000);
    role.overrides.user = currentUser;
  };

  const onChangeStartTime = (role, timeString) => {
    const epochSeconds = timeStringToEpoch(timeString, timezone);
    role.startTime = epochSeconds;

    // If end time exists and is now earlier than start time, add a day to end time
    if (role.endTime && role.endTime <= epochSeconds) {
      role.endTime = role.endTime + SECONDS_IN_A_DAY;
    }

    updateRoleOverride(role);
    role.overrides.overrideKeyValueMap.startTime = epochSeconds + '';
    if (role.endTime) {
      role.overrides.overrideKeyValueMap.endTime = role.endTime + '';
    }
    setPlan({ ...plan });
  };

  const onChangeEndTime = (role, timeString) => {
    let epochSeconds = timeStringToEpoch(timeString, timezone);

    // If end time is earlier than or equal to start time, add a day (span to next day)
    if (role.startTime && epochSeconds <= role.startTime) {
      epochSeconds += SECONDS_IN_A_DAY;
    }

    role.endTime = epochSeconds;
    updateRoleOverride(role);
    role.overrides.overrideKeyValueMap.endTime = epochSeconds + '';
    setPlan({ ...plan });
  };

  return (
    <Column width="100%">
      <Text type={'h400'}>New Hires</Text>
      <Text type={'b400'}>Verify or update the values below and submit.</Text>
      <NewHiresComponent
        shiftSchedules={shiftSchedules}
        newHireRoles={newHireRoles}
        onAddHeadcountOverride={onAddHeadcountOverride}
        onDeleteHeadcountOverride={onDeleteHeadcountOverride}
        onChangeHeadcount={onChangeHeadcount}
        onChangeProcessRole={onChangeProcessRole}
        onChangeStartTime={onChangeStartTime}
        onChangeEndTime={onChangeEndTime}
        isGroceryEnabled={isGroceryEnabled}
        isNarrowWindowsEnabled={isNarrowWindowsEnabled}
        defaultStartTime={plan.shiftPlanHorizonStartTime}
        timezone={timezone}
      />
      <NavigationButtons onBack={onGoBack} onSubmit={onSubmit} onCancel={onCancel} showCancel={true} />
    </Column>
  );
};

const NewHiresComponent = ({
  shiftSchedules,
  newHireRoles,
  onAddHeadcountOverride,
  onDeleteHeadcountOverride,
  onChangeProcessRole,
  onChangeHeadcount,
  onChangeStartTime,
  onChangeEndTime,
  isGroceryEnabled,
  isNarrowWindowsEnabled,
  defaultStartTime,
  timezone
}) => {
  if (!isNarrowWindowsEnabled && !shiftSchedules[0]) {
    return <div />;
  }

  // Initialize time fields based on feature flag
  newHireRoles.forEach((role) => {
    if (isNarrowWindowsEnabled) {
      // Initialize with default times from plan if not already set
      if (!role.startTime || !role.endTime) {
        role.startTime = defaultStartTime;
        role.endTime = defaultStartTime + 2 * SECONDS_IN_AN_HOUR;
      }
    } else {
      // Use shift-based times for backward compatibility
      role.shift = {
        shiftHalfStartTime: shiftSchedules[0].startTimestamp,
        shiftHalfEndTime: shiftSchedules[0].endTimestamp,
        name: shiftSchedules[0].label,
        shiftHalf: ''
      };
    }
  });
  const overriddenRoles = newHireRoles.filter((role) => {
    return chainWalk(() => role.overrides.overrideKeyValueMap.headcount, null) != null;
  });
  return (
    <div style={{ maxWidth: '100%' }}>
      <Row>
        <Text type={'h100'}>{isNarrowWindowsEnabled ? 'Add new hire override' : shiftSchedules[0].label}</Text>
        <div onClick={onAddHeadcountOverride} style={{ cursor: 'pointer' }}>
          <Text>
            <Icon tokens={plusCircleTokens} />
          </Text>
        </div>
      </Row>
      <div style={{ height: 1, marginTop: 30, marginBottom: 30, background: 'rgba(0,0,0,0.1)' }} />
      {overriddenRoles.map((role) => {
        const headcount = chainWalk(() => role.overrides.overrideKeyValueMap.headcount, 0);
        const minHc = chainWalk(() => role.overrides.overrideKeyGuardrailsMap.headcount.minimum, 0);
        const maxHc = chainWalk(() => role.overrides.overrideKeyGuardrailsMap.headcount.maximum, 0);
        const isValid = minHc <= headcount && headcount <= maxHc;
        const startTime = role.startTime || 0;
        const endTime = role.endTime || 0;

        return (
          <Column key={role.name}>
            <Row>
              <Input
                value={headcount}
                onChange={(val) => onChangeHeadcount(role, val)}
                label="New Hires(s) *"
                type="number"
                size="xlarge"
                error={!isValid}
                errorMessage={!isValid ? `Headcount value must be between ${minHc} and ${maxHc}` : ''}
              />
              {isGroceryEnabled ? (
                <Select
                  value={role.role.name}
                  onChange={(val) => onChangeProcessRole(role, val)}
                  width={300}
                  label="Process Path *"
                  placeholder="Select a path"
                  size="xlarge">
                  {Array.from(new Set(newHireRoles.map((r) => r.role.businessType))).map((category) => {
                    const options = newHireRoles
                      .filter(
                        (r) =>
                          r.role.businessType === category &&
                          (r.role.name === role.role.name ||
                            chainWalk(() => r.overrides.overrideKeyValueMap.headcount, null) == null)
                      )
                      .map((r) => <SelectOption key={`${r.role.roleName}`} value={r.role.name} label={r.role.label} />);

                    return (
                      <SelectOptionGroup key={category} label={displayBusinessTabHeader(category)}>
                        {options}
                      </SelectOptionGroup>
                    );
                  })}
                </Select>
              ) : (
                <Select
                  value={role.role.name}
                  onChange={(val) => onChangeProcessRole(role, val)}
                  width={300}
                  label="Process Path *"
                  placeholder="Select a path"
                  size="xlarge">
                  {newHireRoles
                    .filter(
                      (r) =>
                        r.role.name === role.role.name ||
                        chainWalk(() => r.overrides.overrideKeyValueMap.headcount, null) == null
                    )
                    .map((r) => {
                      return <SelectOption key={`${r.role.roleName}`} value={r.role.name} label={r.role.label} />;
                    })}
                </Select>
              )}
              {isNarrowWindowsEnabled && (
                <>
                  <TimePicker
                    value={epochToTimeString(startTime, timezone)}
                    onChange={(timeString) => onChangeStartTime(role, timeString)}
                    width={150}
                    format={{ hourCycle: 'h12', hour: '2-digit', minute: '2-digit' }}
                    size="xlarge"
                    label="Start Time *"
                    showIcon={false}
                  />
                  <TimePicker
                    value={epochToTimeString(endTime, timezone)}
                    onChange={(timeString) => onChangeEndTime(role, timeString)}
                    width={150}
                    format={{ hourCycle: 'h12', hour: '2-digit', minute: '2-digit' }}
                    size="xlarge"
                    label="End Time *"
                    showIcon={false}
                  />
                </>
              )}
              <div onClick={() => onDeleteHeadcountOverride(role)} style={{ cursor: 'pointer' }}>
                <Text>
                  <Icon tokens={minusCircleTokens} />
                </Text>
              </div>
            </Row>
          </Column>
        );
      })}
    </div>
  );
};

export default VerifyNewHires;
